import { useState, useCallback } from 'react';
import { Upload, X, ExternalLink } from 'lucide-react';
import { fileTypeIcon } from '@/lib/fileTypeIcon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatBytes } from '@/lib/formatBytes';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from '@/hooks/useToast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFileSignedUrl } from '@/hooks/useVaultMutations';
import { captureException } from '@/lib/sentry';

export interface ExistingFile {
  id: string;
  name: string;
  file_path: string;
  file_size: number;
  content_type: string;
}

export interface FileAttachment {
  id: string;
  file?: File;
  existingFile?: ExistingFile;
  preview?: string;
}

interface FileAttachmentsProps {
  value?: FileAttachment[];
  onChange?: (files: FileAttachment[]) => void;
  onDeleteExisting?: (attachmentId: string) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
  className?: string;
  /**
   * Show the list of added files and the counter under the drop zone.
   * Default true; turn off when the caller renders its own per-file rows.
   */
  showList?: boolean;
  disabled?: boolean;
}

export function FileAttachments({
  value = [],
  onChange,
  onDeleteExisting,
  maxFiles = 10,
  acceptedTypes = ['image/*', 'application/pdf', '.doc', '.docx', '.xls', '.xlsx'],
  className,
  showList = true,
  disabled = false,
}: FileAttachmentsProps) {
  const { t } = useTranslation();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;
  const signedUrl = useFileSignedUrl();
  const [isDragging, setIsDragging] = useState(false);

  const handleFiles = useCallback((files: FileList | null) => {
    if (!files || disabled) return;

    const newFiles: FileAttachment[] = [];
    const filesArray = Array.from(files);

    // Check if adding these files would exceed maxFiles
    if (value.length + filesArray.length > maxFiles) {
      toast.error(t('fileAttachments.tooMany', { count: maxFiles }));
      return;
    }

    filesArray.forEach((file) => {
      const id = `new-${crypto.randomUUID()}`;

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const attachment: FileAttachment = {
            id,
            file,
            preview: e.target?.result as string,
          };
          newFiles.push(attachment);
          if (newFiles.length === filesArray.length) {
            onChange?.([...value, ...newFiles]);
          }
        };
        reader.readAsDataURL(file);
      } else {
        newFiles.push({ id, file });
        if (newFiles.length === filesArray.length) {
          onChange?.([...value, ...newFiles]);
        }
      }
    });
  }, [value, onChange, maxFiles, disabled, t]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  }, [handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFiles]);

  const removeFile = useCallback((attachment: FileAttachment) => {
    // If it's an existing file from database, call the delete handler
    if (attachment.existingFile && onDeleteExisting) {
      onDeleteExisting(attachment.id);
    }
    // Remove from local state regardless
    onChange?.(value.filter(f => f.id !== attachment.id));
  }, [value, onChange, onDeleteExisting]);

  const getFileIcon = (attachment: FileAttachment) => {
    const Icon = fileTypeIcon(
      attachment.file?.type || attachment.existingFile?.content_type,
      attachment.file?.name || attachment.existingFile?.name,
    );
    return <Icon className="h-4 w-4" />;
  };

  const getFileName = (attachment: FileAttachment) => {
    return attachment.file?.name || attachment.existingFile?.name || t('fileAttachments.unknownFile');
  };

  const getFileSize = (attachment: FileAttachment) => {
    return attachment.file?.size || attachment.existingFile?.file_size || 0;
  };

  /**
   * A new (unsaved) file opens from an object URL. A saved one gets a
   * short-lived signed URL from the Vault's id-keyed Hono route
   * (`useFileSignedUrl`) — the same mint the Vault grid uses, authorized
   * server-side against the file row, so this primitive never talks to
   * Supabase directly. Failures are reported to Sentry and toasted.
   */
  const handlePreview = useCallback(async (attachment: FileAttachment) => {
    if (attachment.file) {
      const objectUrl = URL.createObjectURL(attachment.file);
      window.open(objectUrl, '_blank');
      // Revoke after a delay so the browser can open it first.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      return;
    }

    const fileId = attachment.existingFile?.id;
    if (!fileId || !organizationId) return;
    try {
      const { url } = await signedUrl.mutateAsync({ organizationId, fileId });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      captureException(error, { context: 'FileAttachments preview', fileId });
      toast.error(t('vault.toast.urlFailed'));
    }
  }, [organizationId, signedUrl, t]);

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          "relative border-2 border-dashed rounded-md transition-colors",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
          value.length === 0 || !showList ? "flex-1" : "flex-shrink-0",
          isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/25",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary/50"
        )}
      >
        <input
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileInput}
          disabled={disabled}
          aria-label={t('fileAttachments.inputLabel')}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
        <div className={cn(
          "flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground",
          (value.length === 0 || !showList) && "h-full"
        )}>
          <Upload className="h-4 w-4" />
          <span className="hidden sm:inline">{t('fileAttachments.dropOrClick')}</span>
          <span className="sm:hidden">{t('fileAttachments.tapToUpload')}</span>
        </div>
      </div>

      {/* File List */}
      {showList && value.length > 0 && (
        <div className="space-y-1.5 flex-1 overflow-y-auto mt-2">
          {value.map((attachment) => (
            <div
              key={attachment.id}
              className="flex items-center gap-2 p-2 bg-muted/50 rounded-md text-sm group"
            >
              <div className="flex-shrink-0 text-muted-foreground">
                {getFileIcon(attachment)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium text-xs">
                  {getFileName(attachment)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatBytes(getFileSize(attachment))}
                </p>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePreview(attachment)}
                  className="h-6 w-6 p-0"
                  title={t('fileAttachments.openFile', { name: getFileName(attachment) })}
                  aria-label={t('fileAttachments.openFile', { name: getFileName(attachment) })}
                >
                  <ExternalLink className="h-3 w-3" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(attachment)}
                  disabled={disabled}
                  className="h-6 w-6 p-0"
                  title={t('fileAttachments.removeFile', { name: getFileName(attachment) })}
                  aria-label={t('fileAttachments.removeFile', { name: getFileName(attachment) })}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showList && value.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2 flex-shrink-0">
          {t('fileAttachments.counter', { count: value.length, max: maxFiles })}
        </p>
      )}
    </div>
  );
}
